import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { staffChangePassword } from '@/lib/api';
import { useErrorMessage } from '@/lib/error-message';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore, type Lang, type ThemePreference } from '@/stores/ui.store';

const LANGS: readonly Lang[] = ['ar', 'en'];
const THEMES: readonly ThemePreference[] = ['light', 'dark', 'system'];
const MIN_PASSWORD_LENGTH = 8;

/**
 * Profile — `/settings` (05 §2.13): who am I, my language and theme, and my
 * password.
 *
 * Changing the password revokes **every** refresh token of the user server-side,
 * so the session this tab is holding is finished the moment the call succeeds.
 * Rather than let the TA discover that through a surprise 401 twenty minutes
 * later, the page clears the session itself and sends them back to `/login`.
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toMessage = useErrorMessage();

  const actor = useAuthStore((state) => state.actor);
  const clear = useAuthStore((state) => state.clear);

  const lang = useUiStore((state) => state.lang);
  const setLang = useUiStore((state) => state.setLang);
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);

  useDocumentTitle(t('profile.title'));

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function validate(): boolean {
    const next: typeof errors = {};
    if (currentPassword === '') next.currentPassword = t('validation.required');
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      next.newPassword = t('validation.minLength', { n: MIN_PASSWORD_LENGTH });
    } else if (newPassword === currentPassword) {
      next.newPassword = t('profile.password.sameAsOld');
    }
    if (confirmPassword !== newPassword) next.confirmPassword = t('validation.passwordsDiffer');

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setFormError(null);
    if (!validate()) return;

    setPending(true);
    try {
      await staffChangePassword({ currentPassword, newPassword });
      toast.success(t('profile.password.success'), t('profile.password.successDetail'));
      clear();
      navigate('/login', { replace: true });
    } catch (error) {
      setFormError(toMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <PageHeader title={t('profile.title')} description={t('profile.description')} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.account')}</CardTitle>
            <CardDescription>{t('profile.accountDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('profile.name')}</dt>
                <dd className="font-medium">{actor?.name ?? '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">{t('profile.email')}</dt>
                <dd className="font-medium" dir="ltr">
                  {actor?.email ?? '—'}
                </dd>
              </div>
              <div className="flex items-center gap-2">
                <dt className="text-muted-foreground">{t('profile.role')}</dt>
                <dd>
                  {actor ? (
                    <Badge variant={actor.role === 'ADMIN' ? 'default' : 'secondary'}>
                      {t(`user.role.${actor.role}`)}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.preferences')}</CardTitle>
            <CardDescription>{t('profile.preferencesDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('profile.language')}</p>
              <div className="flex gap-2">
                {LANGS.map((value) => (
                  <Button
                    key={value}
                    variant={value === lang ? 'default' : 'outline'}
                    size="sm"
                    aria-pressed={value === lang}
                    data-testid={`profile-lang-${value}`}
                    onClick={() => setLang(value)}
                  >
                    {t(`language.${value}`)}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">{t('profile.theme')}</p>
              <div className="flex flex-wrap gap-2">
                {THEMES.map((value) => (
                  <Button
                    key={value}
                    variant={value === theme ? 'default' : 'outline'}
                    size="sm"
                    aria-pressed={value === theme}
                    data-testid={`profile-theme-${value}`}
                    onClick={() => setTheme(value)}
                  >
                    {t(`theme.${value}`)}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.password.title')}</CardTitle>
          <CardDescription>{t('profile.password.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="max-w-md space-y-4" onSubmit={onSubmit} noValidate>
            <FormField
              label={t('profile.password.current')}
              htmlFor="current-password"
              required
              {...(errors.currentPassword !== undefined ? { error: errors.currentPassword } : {})}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  dir="ltr"
                  autoComplete="current-password"
                  data-testid="profile-current-password"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  disabled={pending}
                />
              )}
            </FormField>

            <FormField
              label={t('profile.password.new')}
              htmlFor="new-password"
              required
              description={t('validation.minLength', { n: MIN_PASSWORD_LENGTH })}
              {...(errors.newPassword !== undefined ? { error: errors.newPassword } : {})}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  data-testid="profile-new-password"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={pending}
                />
              )}
            </FormField>

            <FormField
              label={t('profile.password.confirm')}
              htmlFor="confirm-password"
              required
              {...(errors.confirmPassword !== undefined ? { error: errors.confirmPassword } : {})}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  data-testid="profile-confirm-password"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={pending}
                />
              )}
            </FormField>

            {formError ? (
              <Alert variant="destructive" data-testid="profile-password-error">
                {formError}
              </Alert>
            ) : null}

            <Alert variant="info">{t('profile.password.revokesSessions')}</Alert>

            <Button type="submit" disabled={pending} data-testid="profile-password-submit">
              {pending ? <Spinner className="size-4" /> : null}
              {t('profile.password.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
